import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChildren,
} from '@angular/core';
import { AuthService, DataService, Broadcaster } from '../../shared/services';
import { Router } from '@angular/router';
import { GridOptions } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { GridComponent } from '../../shared';
import { RESP_GROUP_PLANNING } from '../../shared/constants';
import { SELECTED_PLANNING_GROUPS } from '../../shared/events';

import * as _ from 'lodash';

@Component({
  selector: 'app-fsl-groups',
  templateUrl: './fsl-groups.component.html',
})
export class RoutingFslGroupsComponent implements OnInit, AfterViewInit {
  @Input() frequency?: number;
  @Input() state?: number;
  @Input() labour?: number;

  @Output() selectItems = new EventEmitter();

  @ViewChildren(GridComponent) grids?: GridComponent;
  grid?: any;

  // Available items
  items: any[] = [];
  allItems: any[] = [];
  selectedPlanningGroupIds: any[] = [];

  // Items (assets & tags associated to a specific location)
  selectedNodes: any[] = [];

  // UI flag
  isLoading = false;

  // Local grid options
  gridOptions?: GridOptions;

  taskFilterOptions = [
    {
      name: this.translate.instant('headers.routing.task.task_filter_all'),
      value: 'all',
    },
    {
      name: this.translate.instant('headers.routing.task.task_filter_complete'),
      value: 'complete',
    },
    {
      name: this.translate.instant(
        'headers.routing.task.task_filter_incomplete'
      ),
      value: 'incomplete',
    },
  ];

  filterPlanningGroupAllOption = this.translate.instant(
    'headers.routing.task.planning_group_all'
  );

  respGroupPlanningGroupArr = [];
  selectedFilterType = '';

  FILTER_BLANK_OPTION_ID = 0;

  blankOption: any = [
    {
      id: this.FILTER_BLANK_OPTION_ID,
      description: '(Blank)',
    },
  ];

  constructor(
    private translate: TranslateService,
    private data: DataService,
    public auth: AuthService,
    public router: Router,
    private broadcaster: Broadcaster
  ) {}

  ngAfterViewInit(): void {
    // @ts-ignore
    this.grid = this.grids?._results[0];
  }

  /**
   */
  async ngOnInit(): Promise<void> {
    this.gridOptions = {
      getRowId: (row: any) =>
        row.data.state_id +
        '_' +
        row.data.labour_id +
        '_' +
        row.data.frequency_id,
      suppressRowClickSelection: false,
      rowSelection: 'single',
      rowHeight: 30,
      headerHeight: 30,
      animateRows: true,
      floatingFiltersHeight: 30,
      enableCellTextSelection: false,
      suppressContextMenu: true,
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        floatingFilter: true,
      },
      columnDefs: [
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.frequency.frequency'
          ),
          field: 'frequency',
          checkboxSelection: true,
          hide: false,
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.state.state'
          ),
          field: 'state',
          hide: false,
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.labor.labor'
          ),
          field: 'labour',
          hide: false,
        },
        {
          headerName: this.translate.instant('headers.routing.fsl.tasks'),
          field: 'tasks',
          hide: false,
        },
        {
          headerName: this.translate.instant(
            'headers.routing.fsl.unplanned_tasks'
          ),
          field: 'unplanned_tasks',
          hide: false,
        },
        {
          headerName: this.translate.instant(
            'headers.routing.fsl.planned_tasks%'
          ),
          field: 'planned_tasks_percentage',
          hide: false,
          cellRenderer: (params: any) => {
            return params.data.planned_tasks_percentage + '%';
          },
        },
        {
          headerName: this.translate.instant('headers.routing.fsl.duration'),
          field: 'duration',
          hide: false,
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.frequency.id'
          ),
          field: 'frequency_id',
          hide: true,
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.state.id'
          ),
          field: 'state_id',
          hide: true,
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.labor.id'
          ),
          field: 'labour_id',
          hide: true,
        },
      ],
    };
    await this.loadItems();
  }

  async loadGroupItems(silent = false): Promise<void> {
    if (!silent) {
      this.isLoading = true;
    }
    this.allItems = await this.data
      .post('api/fastpm/modelTasks/fsl-groups', {
        site: this.auth.getSiteId(),
        planningGroupIds: this.selectedPlanningGroupIds,
      })
      .toPromise();
    this.items = this.allItems;
    if (this.selectedFilterType) {
      this.onChangeFilterType(this.selectedFilterType, true);
    }
    this.isLoading = false;
    this.grid?.uncheckAll();
  }
  /**
   * LOADERS
   */
  async loadItems(silent = false): Promise<void> {
    await this.loadGroupItems();
    if (!silent) {
      this.isLoading = true;
    }
    const respGroups = await this.data
      .by('api/config/responsible_groups', {
        criteria: {
          site: [this.auth.getSiteId()],
        },
      })
      .toPromise();

    this.respGroupPlanningGroupArr = respGroups.filter(
      (item: any) => item.type === RESP_GROUP_PLANNING
    );
    this.respGroupPlanningGroupArr = [
      ...this.blankOption,
      ...this.respGroupPlanningGroupArr,
    ] as any;

    this.addSelectAllGroupToItems();
    if (!silent) {
      this.isLoading = false;
    }
  }

  onFilterChanged(event: any): void {}

  onSelectionChanged(event: any): void {
    this.selectedNodes = event;
    this.selectItems.emit(event);
  }

  myCellCallback = (params: any) => {
    return params.value;
  };

  onChangeFilterType(event: any, filterAlreadySelected: boolean = false): void {
    this.selectedFilterType = filterAlreadySelected
      ? event
      : event[0].data.value;
    this.applyFilters();
  }

  async onChangePlanningGroupFilterType(event: any): Promise<void> {
    if (event && event.type === 'change') {
      return;
    }
    await this.loadGroupItems();
    this.applyFilters();
    this.broadcaster.broadcast(
      SELECTED_PLANNING_GROUPS,
      this.selectedPlanningGroupIds
    );
  }

  applyFilters(): void {
    let filteredData = this.allItems;
    if (this.selectedFilterType === 'complete') {
      filteredData = filteredData.filter(
        (row, index) => row.tasks === row.planned_tasks
      );
    } else if (this.selectedFilterType === 'incomplete') {
      filteredData = filteredData.filter(
        (row, index) => row.tasks !== row.planned_tasks
      );
    }
    this.items = filteredData;
  }

  addSelectAllGroupToItems(): void {
    this.respGroupPlanningGroupArr.forEach((element: any) => {
      element.selectAll = 'selectAll';
    });
  }
}
